import './CoralDomainFlipCard.scss'
import '../FlipCard.scss'

import { mapCoralWebDomainFlipCard } from '@websrc/DataMappers/mapSpot'
import React, { useEffect, useRef, useState } from 'react'

import { FlipCard } from '../FlipCard'
type CoralDomainFlipCardProps = ReturnType<typeof mapCoralWebDomainFlipCard>

export const CoralDomainFlipCard = (props: CoralDomainFlipCardProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const isHoveredRef = useRef(isHovered)

  const defaultTime = 0.5
  const handleHoverChange = (hovered: boolean) => {
    setIsHovered(hovered)
  }

  const handleFlipStart = (isMidpoint: boolean) => {
    if (!videoRef.current) return
    const video = videoRef.current

    videoRef.current?.pause()
    if (isMidpoint) video.currentTime = defaultTime
  }

  const handleFlipEnd = (delayDone: boolean) => {
    if (!videoRef.current) return

    const video = videoRef.current

    if (delayDone) {
      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked)
        if (isHoveredRef.current) {
          video.play()
        }
      }

      video.addEventListener('seeked', onSeeked)
      video.currentTime = defaultTime
    }
  }

  const video = props.asset?.video
  const isAnimation = video?.type === 'animation'
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    isHoveredRef.current = isHovered

    const safeSeekToDefault = () => {
      try {
        video.currentTime = defaultTime
      } catch (err) {
        console.warn('Seek failed, falling back to 0s', err)
      }
    }

    const handleLoadedData = () => {
      video.removeEventListener('loadeddata', handleLoadedData)
      if (!isHoveredRef.current) {
        safeSeekToDefault()
      }
    }

    if (isHovered) {
      video.play().catch((err) => {
        console.warn('Autoplay failed (probably mobile/tablet)', err)
      })
    } else {
      video.pause()
      if (video.readyState >= 2) {
        safeSeekToDefault()
      } else {
        video.addEventListener('loadeddata', handleLoadedData)
      }
    }

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData)
    }
  }, [isHovered])

  const content = {
    frontHeader: (
      <div className={'coral-domain-flip-card__headline-container'}>
        <div className={'coral-domain-flip-card__headline'}>{props.header}</div>
        <div className={'coral-domain-flip-card__front-description'}>{props.front_description}</div>
      </div>
    ),
    frontContent: (
      <div className={`coral-domain-flip-card__front-image-container`}>
        {isAnimation && video?.url ? (
          <video
            autoPlay={false}
            className={`coral-domain-flip-card__front-image`}
            loop={true}
            muted={true}
            playsInline={true}
            ref={videoRef}
            src={video.url}
            title={video.title}
          />
        ) : (
          <div>No animation found</div>
        )}
      </div>
    ),
    backHeader: (
      <div className={'coral-domain-flip-card__headline-container'}>
        <div className={'coral-domain-flip-card__headline'}>{props.header}</div>
        <div className={'coral-domain-flip-card__back-description'}>{props.back_description}</div>
      </div>
    ),
    backContent: (
      <div className={'coral-domain-flip-card__back-content'}>
        {props.badges_group.map((group, groupId) => (
          <div className={'coral-domain-flip-card__back-content-wrapper'} key={groupId}>
            <div className={'coral-domain-flip-card__badge-title'}>{group.badges_title}</div>
            <div className={'coral-domain-flip-card__badge'}>
              {group.badges.map((badge, badgeId) => (
                <div
                  className={`coral-domain-flip-card__badge-name m--${group.visuals}`}
                  key={badgeId}
                >
                  {badge}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    ),
  }

  return (
    <FlipCard
      animateFixedMobile={400}
      animateFixedTablet={'auto'}
      animateHeight={true}
      buttonColor={props.button_visuals}
      className={'coral-domain-flip-card'}
      content={content}
      onFlipEndChange={handleFlipEnd}
      onFlipStartChange={handleFlipStart}
      onHoverChange={handleHoverChange}
    />
  )
}
